import {action, observable} from 'mobx';
import filter from 'lodash/filter';
import map from 'lodash/map';
import {ITsInterfaceHelperService} from '../../services/ts-interface-helper/ts-interface-helper.service';

export interface TsMultiSelectConfig {
    id?: string;
    inputId?: string;
    controlName?: string;
    inputModel: any[];
    outputModel?: any;
    selectedProperty?: string;
    outputProperties?: string[];
    buttonLabel?: string[];
    itemLabel?: string[];
    placeholder?: string;
    onChange?: (output: any) => void;
}

export class TsMultiSelectController {
    @observable isOpen = false;
    @observable buttonText = '';

    private selectedItem: any = null;
    private outputPropsArr: string[] | undefined;
    private buttonLabelArr: string[] = [];
    private itemLabelArr: string[] = [];
    private useNewInterface: boolean;
    public config: TsMultiSelectConfig;

    constructor(useNewInterface: boolean = false, config: TsMultiSelectConfig) {
        this.useNewInterface = useNewInterface;
        this.config = config;

        this.initializeFromConfig();
    }

    private initializeFromConfig(): void {
        this.initializeArrays();
        this.updateButtonText();
    }

    private initializeArrays(): void {
        this.outputPropsArr = Array.isArray(this.config.outputProperties)
            ? this.config.outputProperties
            : this.config.outputProperties
              ? String(this.config.outputProperties).trim().split(/\s+/)
              : undefined;

        this.buttonLabelArr = Array.isArray(this.config.buttonLabel)
            ? this.config.buttonLabel
            : String((this.config.buttonLabel as any) ?? 'name')
                  .trim()
                  .split(/\s+/);

        this.itemLabelArr = Array.isArray(this.config.itemLabel)
            ? this.config.itemLabel
            : String((this.config.itemLabel as any) ?? 'name')
                  .trim()
                  .split(/\s+/);
    }

    isBrightMultiSelect(): boolean {
        return this.useNewInterface || this.config.inputId === 'big-label' || this.config.inputId === 'login';
    }

    @action
    updateButtonText(): void {
        if (this.selectedItem) {
            this.buttonText = this.buttonLabelArr.map((label: string) => this.selectedItem[label]).join(' ');
            return;
        }

        // Check for default selection from outputModel
        let selectNameDef = '';
        this.config?.inputModel?.forEach((item) => {
            if (item[this.config.outputModel]) {
                selectNameDef = item[this.config.outputModel];
                this.selectedItem = item;
            } else if (item.value === this.config.outputModel) {
                selectNameDef = item.name;
                this.selectedItem = item;
            }
        });

        this.buttonText = selectNameDef || this.config.placeholder || 'Select Item...';
    }

    @action
    selectItem = (item: any): void => {
        this.selectedItem = item;

        if (this.config && this.config.onChange) this.config.onChange(item);

        this.updateButtonText();
    };

    @action
    ensureSelectedItem(): void {
        if (!this.selectedItem && this.config.inputModel.length > 0) {
            this.selectItem(this.config.inputModel[0]);
        }
    }

    @action
    toggleOpen(): void {
        this.isOpen = !this.isOpen;
    }

    @action
    close(): void {
        this.isOpen = false;
    }

    getHiddenValue(): string {
        return this.buttonText;
    }

    getItemLabelArr(): string[] {
        return this.itemLabelArr;
    }

    getSelectedProperty(): string {
        return this.config.selectedProperty || 'selected';
    }

    // Method to update config after initialization
    updateConfig(newConfig: Partial<TsMultiSelectConfig>): void {
        this.config = {...this.config, ...newConfig};
        this.initializeFromConfig();
    }

    // Getter methods for component access
    getInputModel(): any[] {
        return this.config.inputModel || [];
    }

    getInputId(): string {
        return this.config.inputId || '';
    }

    getControlName(): string {
        return this.config.controlName || '';
    }

    getSelectionMode(): string {
        return 'single';
    }
}
